import React from 'react';
import checkmark from '../../images/checkmarkIcon.svg';

export default function BulletList({
  items = [],
  delayMs = 500,
  theme = 'dark',
  className = '',
  instant = false,
  onDone,             // NEW: notify parent when the sequence finishes
}) {
  const isDark = theme === 'dark';
  const bulletText = isDark ? 'text-gray-200' : 'text-gray-800';

  const [visibleCount, setVisibleCount] = React.useState(instant ? items.length : 0);
  const timeoutsRef = React.useRef([]);

  // Stable content signature to avoid restarting animation on identical content
  const itemsKey = React.useMemo(() => {
    try { return JSON.stringify(items ?? []); } catch { return String(items?.length || 0); }
  }, [items]);

  React.useEffect(() => {
    // instant mode: show all, no timers
    if (instant) {
      setVisibleCount(items.length);
      return;
    }

    setVisibleCount(0);
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];

    items.forEach((_, i) => {
      const t = setTimeout(
        () => setVisibleCount(c => Math.max(c, i + 1)),
        (i + 1) * delayMs
      );
      timeoutsRef.current.push(t);
    });

    return () => {
      timeoutsRef.current.forEach(t => clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, [itemsKey, delayMs, instant]);

  // Notify when all bullets are shown (first run)
  React.useEffect(() => {
  if (!instant && visibleCount >= items.length && items.length > 0) {
  onDone && onDone();
  }
  }, [visibleCount, items.length, instant, onDone]);


  if (!items?.length) return null;

  return (
    <ul
      className={`mt-5 space-y-3 w-full max-w-[520px] mx-auto px-4 sm:px-6 ${className}`}
    >
      {items.map((text, i) => {
        const shown = i < visibleCount;

        // Deterministic metrics: fixed icon size and line-height
        return (
          <li
            key={i}
            className={`flex items-center gap-3 ${bulletText}`}
            style={{
              opacity: shown ? 1 : 0,
              transform: shown ? 'translateY(0px)' : 'translateY(6px)',
              transition: instant ? 'none' : 'opacity 400ms ease, transform 400ms ease',
            }}
          >
            <img
              src={checkmark}
              alt=""
              className="w-3.5 h-3.5 flex-none"      // slightly smaller icon
              aria-hidden="true"
            />
            <span className="text-sm leading-[1.4]">{text}</span>  {/* fixed leading */}
          </li>
        );
      })}
    </ul>
  );
}
